// ProductsPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Space,
  Spin,
  Alert,
  Badge,
  Divider,
  Tooltip,
  theme,
} from 'antd';
import {
  ShoppingCartOutlined,
  DollarOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  StarOutlined,
  FireOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import { useNavigate } from 'react-router';
import { useLanguage } from '../providers/LanguageProvider';

const { Title, Text, Paragraph } = Typography;

export default function ProductsPage() {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState({});

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/licenses-products');
      const allProducts = response.data?.licenseProducts || [];
      
      // Ana ürünleri filtrele (Wholesaler Parts-Soft ve Webshop B2B)
      const mainProducts = allProducts.filter(product => 
        product.Name === 'Wholesaler Parts-Soft' || product.Name === 'Webshop B2B'
      );
      
      // Her ana ürün için alt ürünleri ekle
      const organizedProducts = mainProducts.map(mainProduct => {
        const subProducts = allProducts.filter(product => 
          product.Parent === mainProduct.Id
        );
        return {
          ...mainProduct,
          subProducts: subProducts
        };
      });
      
      setProducts(organizedProducts);
    } catch (err) {
      setError(t('PRODUCTS_LOAD_ERROR', 'Ürünler yüklenirken bir hata oluştu.'));
      console.error('Products fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price === 0) return t('PRODUCT_FREE', 'Ücretsiz');
    return `$${price}`;
  };

  const getProductType = (product) => {
    if (product.Parent === 0) return t('PRODUCT_MAIN', 'Ana Ürün');
    return t('PRODUCT_ADDON', 'Eklenti');
  };

  const getProductColor = (product) => {
    if (product.Parent === 0) return 'blue';
    return 'green';
  };

  const getProductIcon = (product) => {
    if (product.Parent === 0) return <StarOutlined />;
    return <CheckCircleOutlined />;
  };



  const handlePurchase = (product) => {
    console.log('Satın al:', product);
    // SubscriptionsPage'e yönlendir ve ürün bilgisini localStorage'a kaydet
    localStorage.setItem('selectedProduct', JSON.stringify(product));
    navigate('/subscriptions');
  };

  const toggleExpanded = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>{t('PRODUCTS_LOADING', 'Ürünler yükleniyor...')}</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message={t('ERROR_GENERIC', 'Hata')}
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={fetchProducts}>
            {t('BTN_TRY_AGAIN', 'Tekrar Dene')}
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <Title level={2}>{t('PRODUCTS_TITLE', 'Ürünler ve Lisanslar')}</Title>
        <Text type="secondary">
          {t('PRODUCTS_DESCRIPTION', 'İhtiyacınıza uygun ürünleri seçin ve hemen satın alın')}
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        {products.map((mainProduct) => (
          <Col xs={24} key={mainProduct.Id}>
            <Card
              hoverable
              style={{
                borderRadius: 16,
                boxShadow: `0 8px 24px ${token.colorBgElevated}`,
                border: `1px solid ${token.colorBorder}`,
                overflow: 'hidden',
                marginBottom: 24,
                background: token.colorBgContainer,
              }}
              bodyStyle={{ padding: 0 }}
            >
              {/* Ana Ürün Header */}
              <div style={{ 
                background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
                padding: '48px 32px',
                color: 'white',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
              }}
              onClick={() => toggleExpanded(mainProduct.Id)}
              >
                
                <div style={{ position: 'relative', zIndex: 2 }}>
                  <Badge.Ribbon
                    text={t('PRODUCT_MAIN', 'Ana Ürün')}
                    color="gold"
                    style={{ fontSize: 12 }}
                  >
                    <div
                      style={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                        color: 'white',
                        fontSize: 40,
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      {getProductIcon(mainProduct)}
                    </div>
                  </Badge.Ribbon>
                  
                                     <Title level={2} style={{ color: 'white', margin: '0 0 12px 0' }}>
                     {mainProduct.Name}
                   </Title>
                   
                   {mainProduct.Description && (
                     <Text style={{ 
                       color: 'rgba(255,255,255,0.9)', 
                       fontSize: 16, 
                       lineHeight: 1.5,
                       marginBottom: 20,
                       display: 'block',
                       maxWidth: '600px',
                       margin: '0 auto 20px'
                     }}>
                       {mainProduct.Description}
                     </Text>
                   )}
                   
                   <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                     <Text style={{ fontSize: 32, fontWeight: 'bold', color: 'white' }}>
                       {formatPrice(mainProduct.Price)}
                     </Text>
                     {mainProduct.IsMonthly && (
                       <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 18 }}>
                         {t('PRODUCT_PER_MONTH', '/ay')}
                       </Text>
                     )}
                   </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                    <Button
                      type="primary"
                      size="large"
                      ghost
                      icon={<ShoppingCartOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePurchase(mainProduct);
                      }}
                      style={{
                        borderRadius: 8,
                        height: 48,
                        fontSize: 16,
                        fontWeight: 600,
                        borderColor: 'rgba(255,255,255,0.3)',
                        color: 'white',
                      }}
                                         >
                       {t('BTN_BUY', 'Satın Al')}
                     </Button>
                    
                    <Button
                      type="text"
                      size="large"
                      icon={expandedProducts[mainProduct.Id] ? <UpOutlined /> : <DownOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(mainProduct.Id);
                      }}
                      style={{
                        color: 'white',
                        fontSize: 16,
                        height: 48,
                        width: 48,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Alt Ürünler - Açılır/Kapanır */}
              {expandedProducts[mainProduct.Id] && mainProduct.subProducts && mainProduct.subProducts.length > 0 && (
                <div style={{ 
                  padding: '32px',
                  background: token.colorBgLayout,
                  borderTop: `1px solid ${token.colorBorder}`,
                }}>
                  <Title level={3} style={{ margin: '0 0 24px 0', color: token.colorPrimary, textAlign: 'center' }}>
                    {t('PRODUCTS_SUB_PRODUCTS', 'Alt Ürünler ve Eklentiler')}
                  </Title>
                  
                  <Row gutter={[24, 24]}>
                    {mainProduct.subProducts.map((subProduct) => (
                      <Col xs={24} sm={12} md={8} lg={6} key={subProduct.Id}>
                                                 <Card
                           size="small"
                           hoverable
                           style={{
                             borderRadius: 12,
                             border: `1px solid ${token.colorBorder}`,
                             height: '100%',
                             background: token.colorBgContainer,
                             display: 'flex',
                             flexDirection: 'column',
                           }}
                           bodyStyle={{ 
                             padding: 20,
                             display: 'flex',
                             flexDirection: 'column',
                             height: '100%',
                           }}
                         >
                                                     <div style={{ flex: 1 }}>
                             <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                               <div
                                 style={{
                                   width: 48,
                                   height: 48,
                                   borderRadius: '50%',
                                   background: `linear-gradient(135deg, #52c41a 0%, #389e0d 100%)`,
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   marginRight: 16,
                                   color: 'white',
                                   fontSize: 20,
                                 }}
                               >
                                 {getProductIcon(subProduct)}
                               </div>
                               <div style={{ flex: 1 }}>
                                 <Text strong style={{ display: 'block', fontSize: 16, color: token.colorText }}>
                                   {subProduct.Name}
                                 </Text>
                                 <Text type="secondary" style={{ fontSize: 14, color: token.colorTextSecondary }}>
                                   {subProduct.IsMonthly ? t('PRODUCT_MONTHLY', 'Aylık') : t('PRODUCT_ONE_TIME', 'Tek Seferlik')}
                                 </Text>
                                 {subProduct.Description && (
                                   <Text type="secondary" style={{ 
                                     fontSize: 12, 
                                     lineHeight: 1.4,
                                     marginTop: 4,
                                     display: 'block',
                                     opacity: 0.8,
                                     color: token.colorTextSecondary
                                   }}>
                                     {subProduct.Description}
                                   </Text>
                                 )}
                               </div>
                             </div>
                             
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                               <Text style={{ fontSize: 20, fontWeight: 'bold', color: token.colorPrimary }}>
                                 {formatPrice(subProduct.Price)}
                               </Text>
                               <Tag color={subProduct.IsRequired ? 'red' : 'default'} size="small">
                                 {subProduct.IsRequired ? t('PRODUCT_REQUIRED', 'Zorunlu') : t('PRODUCT_OPTIONAL', 'Opsiyonel')}
                               </Tag>
                             </div>
                           </div>
                           
                           <div style={{ marginTop: 'auto' }}>
                             <Button
                               type="default"
                               size="middle"
                               block
                               icon={<ShoppingCartOutlined />}
                               onClick={() => handlePurchase(subProduct)}
                               style={{
                                 borderRadius: 8,
                                 height: 40,
                                 borderColor: token.colorBorder,
                                 color: token.colorText,
                                 background: token.colorBgContainer,
                               }}
                             >
                               {t('BTN_BUY', 'Satın Al')}
                             </Button>
                           </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      {products.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <InfoCircleOutlined style={{ fontSize: 48, color: token.colorTextSecondary, marginBottom: 16 }} />
          <Title level={4} type="secondary" style={{ color: token.colorTextSecondary }}>
            {t('PRODUCTS_EMPTY_TITLE', 'Henüz ürün bulunmuyor')}
          </Title>
          <Text type="secondary" style={{ color: token.colorTextSecondary }}>
            {t('PRODUCTS_EMPTY_DESC', 'Yakında yeni ürünler eklenecek')}
          </Text>
        </div>
      )}
    </div>
  );
}
